// EmployerOnboarding.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/Icons/QuesthireBlack.png";
import PaymentForm from "./PaymentForm";

const steps = [
  { title: "Create a free employer account", type: "account_creation" },
  { title: "Tell us about your company", type: "company_details" },
  { title: "Finalize your details", type: "finalize_details" },
  { title: "Post your job", type: "post_job" }, // New step
  { title: "", type: "payment_form" },
];

const EmployerOnboarding = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const progress = ((stepIndex + 1) / steps.length) * 100;
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col justify-between px-4 pt-8 pb-0">
      {/* Header */}
      <div className="w-full flex justify-start px-10 mb-10 relative">
        <div className="absolute left-10 top-1/2 -translate-y-1/2">
          <img src={logo} alt="logo" className="h-64 w-64 object-contain" />
        </div>
      </div>

      {/* Step Content */}
      <div className="flex flex-col items-center justify-center w-full flex-grow">
        <h2 className="text-3xl font-bold text-center mb-6">
          {currentStep.title}
        </h2>

        {currentStep.type === "account_creation" && (
          <div className="w-full max-w-md">
            <div className="border px-4 py-3 rounded-md shadow-sm mb-4 text-center">
              <button className="w-full py-2 px-4 bg-white border border-gray-300 rounded hover:bg-gray-50">
                Continue as{" "}
                <span className="font-semibold">stephandgraham@gmail.com</span>
              </button>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-gray-500 text-sm">OR</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            <form className="space-y-4">
              <div>
                <label className="block text-left font-semibold text-sm mb-1">
                  Name
                </label>
                <input
                  type="text"
                  placeholder="First Last"
                  className="w-full border border-gray-300 rounded px-4 py-2"
                />
              </div>
              <div>
                <label className="block text-left font-semibold text-sm mb-1">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="email@domain.com"
                  className="w-full border border-gray-300 rounded px-4 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Candidates that apply to your job will be sent to this email
                </p>
              </div>
              <div>
                <label className="block text-left font-semibold text-sm mb-1">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Your password"
                  className="w-full border border-gray-300 rounded px-4 py-2"
                />
              </div>
              <button
                type="submit"
                className="bg-black text-white font-semibold w-full py-2 rounded hover:bg-black transition"
              >
                Get Started
              </button>
            </form>

            <p className="text-sm text-center mt-4">
              Already have an employer account?{" "}
              <span className="underline font-semibold cursor-pointer">
                Sign In
              </span>
            </p>

            <p className="text-xs text-gray-500 text-center mt-4">
              By clicking "Get Started", I agree to the QuestHire{" "}
              <span className="underline">Terms of Use</span> and acknowledge I
              have read the <span className="underline">Privacy Policy</span>.
            </p>
          </div>
        )}

        {currentStep.type === "company_details" && (
          <div className="w-full max-w-md space-y-5">
            <p className="text-sm text-left font-semibold">
              Are you a job seeker looking for a job?{" "}
              <span className="underline text-black cursor-pointer">
                Begin Job Search
              </span>
            </p>

            <div>
              <label className="block font-semibold text-sm mb-1">
                Company name
              </label>
              <input
                type="text"
                placeholder="ABC Company"
                className="w-full border border-gray-300 rounded px-4 py-2"
              />
            </div>

            <div>
              <label className="block font-semibold text-sm mb-1">
                Company website (optional)
              </label>
              <input
                type="text"
                placeholder="www.website.com"
                className="w-full border border-gray-300 rounded px-4 py-2"
              />
            </div>

            <div>
              <label className="block font-semibold text-sm mb-1">
                Zip code
              </label>
              <input
                type="text"
                placeholder="00000"
                className="w-full border border-gray-300 rounded px-4 py-2"
              />
            </div>

            <div>
              <label className="block font-semibold text-sm mb-1">
                Your current title
              </label>
              <select className="w-full border border-gray-300 rounded px-4 py-2">
                <option>Job title</option>
                <option>Hiring Manager</option>
                <option>HR</option>
                <option>Recruiter</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-sm mb-1">
                Phone number
              </label>
              <input
                type="text"
                placeholder="###-###-####"
                className="w-full border border-gray-300 rounded px-4 py-2"
              />
            </div>

            <div className="flex items-start gap-2 text-sm">
              <input type="checkbox" defaultChecked />
              <label>
                I agree to let QuestHire contact me by autodialer at the number
                above
              </label>
            </div>
          </div>
        )}

        {currentStep.type === "finalize_details" && (
          <div className="w-full max-w-md space-y-5">
            <div>
              <label className="block text-sm font-semibold mb-1">
                How did you hear about us?
              </label>
              <select className="w-full border border-gray-300 rounded px-4 py-2">
                <option>Select one</option>
                <option>Google</option>
                <option>Friend</option>
                <option>Social Media</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                How many people do you need to hire?
              </label>
              <select className="w-full border border-gray-300 rounded px-4 py-2">
                <option>Select an option</option>
                <option>1-5</option>
                <option>6-10</option>
                <option>11-20</option>
                <option>20+</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Are you responsible for all hiring or only specific roles?
              </label>
              <select className="w-full border border-gray-300 rounded px-4 py-2">
                <option>Select an option</option>
                <option>All hiring</option>
                <option>Specific roles</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Which of these best describes your hiring needs?
              </label>
              <select className="w-full border border-gray-300 rounded px-4 py-2">
                <option>Hiring needs</option>
                <option>Occasional</option>
                <option>Seasonal</option>
                <option>Ongoing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Do you use an Applicant Tracking System?
              </label>
              <select className="w-full border border-gray-300 rounded px-4 py-2">
                <option>Select an answer</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>
          </div>
        )}

        {currentStep.type === "post_job" && (
          <div className="w-full max-w-2xl space-y-6">
            <div className="bg-green-50 border border-green-200 text-sm text-green-900 px-4 py-3 rounded">
              <strong>💡 80% of employers</strong> who post a job on
              ZipRecruiter get a quality candidate through the site within the
              first day!
            </div>

            <div>
              <label className="font-semibold text-sm">Job Title *</label>
              <input
                type="text"
                placeholder="Enter job title"
                className="w-full border border-gray-300 rounded px-4 py-2 mt-1"
              />
            </div>

            <div>
              <label className="font-semibold text-sm">Job Location *</label>
              <input
                type="text"
                placeholder="Enter ZIP or city, state"
                className="w-full border border-gray-300 rounded px-4 py-2 mt-1"
              />
            </div>

            <div>
              <label className="font-semibold text-sm">Street Address</label>
              <input
                type="text"
                placeholder="123 Main Street"
                className="w-full border border-gray-300 rounded px-4 py-2 mt-1"
              />
            </div>

            <div>
              <label className="font-semibold text-sm block mb-1">
                Can this role be performed as a "remote work from home" job with
                no on-site work required?
              </label>
              <div className="flex gap-4 items-center">
                <label className="flex gap-2 items-center">
                  <input type="radio" name="remote" value="yes" />
                  Yes
                </label>
                <label className="flex gap-2 items-center">
                  <input type="radio" name="remote" value="no" />
                  No
                </label>
              </div>
            </div>

            <div>
              <label className="font-semibold text-sm">Employment Type *</label>
              <select className="w-full border border-gray-300 rounded px-4 py-2 mt-1">
                <option value="">- Select employment type -</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="temporary">Temporary</option>
              </select>
            </div>

            <div>
              <label className="font-semibold text-sm">Job Description *</label>
              <textarea
                rows="6"
                className="w-full border border-gray-300 rounded px-4 py-2 mt-1"
                placeholder="Enter job description"
              ></textarea>
            </div>

            <div>
              <label className="font-semibold text-sm">Pay Information *</label>
              <div className="flex flex-col gap-2 mt-1">
                <label className="flex gap-2 items-center">
                  <input type="radio" name="payInfo" value="custom" /> Provide
                  my own
                </label>
                <label className="flex gap-2 items-center">
                  <input type="radio" name="payInfo" value="estimated" /> Use
                  estimated pay
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="font-semibold text-sm">Pay Type</label>
                <select className="w-full border border-gray-300 rounded px-4 py-2 mt-1">
                  <option>Pay Range</option>
                  <option>Exact Amount</option>
                </select>
              </div>
              <div>
                <label className="font-semibold text-sm">Minimum Pay</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-4 py-2 mt-1"
                  placeholder="$ Min"
                />
              </div>
              <div>
                <label className="font-semibold text-sm">Maximum Pay</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-4 py-2 mt-1"
                  placeholder="$ Max"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-semibold text-sm">Currency</label>
                <select className="w-full border border-gray-300 rounded px-4 py-2 mt-1">
                  <option>USD</option>
                  <option>CAD</option>
                </select>
              </div>
              <div>
                <label className="font-semibold text-sm">Frequency</label>
                <select className="w-full border border-gray-300 rounded px-4 py-2 mt-1">
                  <option>Annually</option>
                  <option>Monthly</option>
                  <option>Hourly</option>
                </select>
                <label className="flex gap-2 mt-2 items-center text-sm">
                  <input type="checkbox" /> Plus Commission
                </label>
              </div>
            </div>

            {/* Preview Box */}
            <div className="border rounded p-4 bg-gray-50 text-sm mt-6">
              <p className="font-bold mb-1">Job Card Preview</p>
              <p className="text-gray-700">
                Your job looks great! This is how job seekers will see the pay
                info you provided. If you'd like to change anything, you can
                edit your input above.
              </p>
            </div>
          </div>
        )}
{currentStep.type === "payment_form" && <PaymentForm/>}
      </div>

      {/* Progress & Navigation */}
      <div className="w-full px-4 border-t border-gray-200">
        <div className="mx-auto py-6">
          <div className="w-full h-1 bg-gray-200 rounded-full mb-6">
            <div
              className="h-full bg-black rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between">
            <button
              disabled={stepIndex === 0}
              onClick={() => setStepIndex(Math.max(stepIndex - 1, 0))}
              className="text-gray-700 underline disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={() => {
                setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
                if (isLastStep) navigate("/employer/post-job");
              }}
              className="bg-black text-white px-6 py-2 rounded hover:bg-white hover:text-black"
            >
              {isLastStep ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerOnboarding;
